import { supabase } from '@/lib/supabase/client';

export type OutreachExternalSyncSource = 'customer_deal' | 'mailmeteor' | 'gdrive';
export type OutreachExternalSyncMode = 'dry_run' | 'apply';
export type OutreachExternalSyncStatus =
  | 'planned'
  | 'dry_run_complete'
  | 'apply_started'
  | 'applied'
  | 'partial_failure'
  | 'failed'
  | 'rolled_back';
export type OutreachFreshnessState = 'fresh' | 'stale' | 'missing' | 'processing' | 'failed' | 'future_inactive';
export type OutreachExportGateState = 'ready' | 'review' | 'blocked';

export interface OutreachExternalSyncFreshnessSource {
  source: OutreachExternalSyncSource;
  label: string;
  description: string;
  requiredForExport: boolean;
  contractVersion: string | null;
  state: OutreachFreshnessState;
  latestStatus: OutreachExternalSyncStatus | null;
  latestMode: OutreachExternalSyncMode | null;
  latestAppliedAt: string | null;
  latestCompletedAt: string | null;
  staleAfter: string | null;
  totalRuns: number;
  rowCount: number | null;
  actionCount: number | null;
  invalidCount: number | null;
  message: string;
}

export interface OutreachExternalSyncFreshnessSnapshot {
  generatedAt: string;
  environment: 'production';
  sources: OutreachExternalSyncFreshnessSource[];
  exportGate: {
    state: OutreachExportGateState;
    requiredFreshSources: OutreachExternalSyncSource[];
    blockers: string[];
    warnings: string[];
  };
}

export interface OutreachSafetyQueueStatusSummary {
  generatedAt: string;
  queued: number;
  processing: number;
  activeProcessing: number;
  failed: number;
  deadLettered: number;
  completed: number;
  rerunRequested: number;
  totalOpen: number;
  nextReadyAt: string | null;
  oldestOpenCreatedAt: string | null;
  hasActiveWorker: boolean;
  hasFailures: boolean;
}

export interface OutreachSafetyManualRefreshResult {
  runId: string;
  scope: 'full' | 'queued';
  claimed: number;
  completed: number;
  failed: number;
  totalTargets: number;
  queue: OutreachSafetyQueueStatusSummary;
}

export interface OutreachSafetyQueueStatusResponse {
  queue: OutreachSafetyQueueStatusSummary;
  applyEnabled: boolean;
}

export type LeadContactReadinessReason =
  | 'missing_person'
  | 'missing_email'
  | 'invalid_email'
  | 'role_email'
  | 'generic_email'
  | 'contact_ready';

export interface LeadContactReadinessClassificationPreview {
  contractVersion: 'lead-contact-readiness-classification-v1';
  unknownCount: number;
  projectedReadyCount: number;
  projectedNeedsContactCount: number;
  reasonCounts: Partial<Record<LeadContactReadinessReason, number>>;
  snapshotToken: string;
  lifecyclePriorityFingerprint: string;
}

export interface LeadContactReadinessClassificationResult {
  contractVersion: 'lead-contact-readiness-classification-v1';
  appliedCount: number;
  readyCount: number;
  needsContactCount: number;
  reasonCounts: Partial<Record<LeadContactReadinessReason, number>>;
  queuedForSafetyCount: number;
  lifecyclePriorityUnchanged: true;
  lifecyclePriorityFingerprint: string;
}

interface ExternalSyncRunRow {
  source: string | null;
  mode: string | null;
  status: string | null;
  source_contract_version: string | null;
  completed_at: string | null;
  applied_at: string | null;
  stale_after: string | null;
  row_counts: unknown;
  action_counts: unknown;
  invalid_counts: unknown;
  created_at: string | null;
}

const FULL_COVERAGE_KIND = 'full_lead_backfill';
const QUEUED_COVERAGE_KIND = 'queued_lead_batch';

const SOURCE_CONFIG: Record<
  OutreachExternalSyncSource,
  { label: string; description: string; requiredForExport: boolean; futureInactiveMessage?: string }
> = {
  customer_deal: {
    label: 'Admin customer/contact/deal check',
    description: 'Current customers, active deals, duplicate leads, and explicit admin suppressions.',
    requiredForExport: true,
  },
  mailmeteor: {
    label: 'MailMeteor suppressions',
    description: 'Future bounce/unsubscribe source. TPB has not used MailMeteor yet.',
    requiredForExport: false,
    futureInactiveMessage: 'Future source; not required for v1.',
  },
  gdrive: {
    label: 'Google Drive safety sheet',
    description: 'Future external safety sheet. No authoritative sheet is active for v1.',
    requiredForExport: false,
    futureInactiveMessage: 'Future source; not required for v1.',
  },
};

const SOURCES = Object.keys(SOURCE_CONFIG) as OutreachExternalSyncSource[];
const statuses: OutreachExternalSyncStatus[] = [
  'planned',
  'dry_run_complete',
  'apply_started',
  'applied',
  'partial_failure',
  'failed',
  'rolled_back',
];
const modes: OutreachExternalSyncMode[] = ['dry_run', 'apply'];

function parseSource(value: unknown): OutreachExternalSyncSource | null {
  return typeof value === 'string' && SOURCES.includes(value as OutreachExternalSyncSource)
    ? (value as OutreachExternalSyncSource)
    : null;
}

function parseStatus(value: unknown): OutreachExternalSyncStatus | null {
  return typeof value === 'string' && statuses.includes(value as OutreachExternalSyncStatus)
    ? (value as OutreachExternalSyncStatus)
    : null;
}

function parseMode(value: unknown): OutreachExternalSyncMode | null {
  return typeof value === 'string' && modes.includes(value as OutreachExternalSyncMode)
    ? (value as OutreachExternalSyncMode)
    : null;
}

function sumCounts(value: unknown): number | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const numbers = Object.values(value as Record<string, unknown>)
    .map((item) => (typeof item === 'number' && Number.isFinite(item) ? item : null))
    .filter((item): item is number => item !== null);
  if (numbers.length === 0) return null;
  return numbers.reduce((total, item) => total + item, 0);
}

function asCountsRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function numberCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function isFullCoverageAppliedRun(row: ExternalSyncRunRow): boolean {
  if (row.status !== 'applied' || row.mode !== 'apply' || !row.applied_at) return false;
  const rowCounts = asCountsRecord(row.row_counts);
  const coverageKind = rowCounts.coverage_kind;
  const totalTargets = numberCount(rowCounts.total_targets);
  const completed = numberCount(rowCounts.completed);
  const failed = numberCount(rowCounts.failed);
  return coverageKind === FULL_COVERAGE_KIND && totalTargets > 0 && completed === totalTargets && failed === 0;
}

function isFullCoverageRun(row: ExternalSyncRunRow): boolean {
  const rowCounts = asCountsRecord(row.row_counts);
  return rowCounts.coverage_kind === FULL_COVERAGE_KIND || isActiveFullCoverageRun(row);
}

function isEmptyQueuedWorkerRun(row: ExternalSyncRunRow): boolean {
  const rowCounts = asCountsRecord(row.row_counts);
  return rowCounts.coverage_kind === QUEUED_COVERAGE_KIND && numberCount(rowCounts.total_targets) === 0;
}

function pickLatestDisplayRun(
  source: OutreachExternalSyncSource,
  rows: ExternalSyncRunRow[],
  latestApplied: ExternalSyncRunRow | null,
): ExternalSyncRunRow | null {
  if (source === 'customer_deal') {
    return rows.find(isActiveFullCoverageRun) ?? latestApplied ?? rows.find((row) => !isEmptyQueuedWorkerRun(row)) ?? null;
  }
  return rows.find((row) => !isEmptyQueuedWorkerRun(row)) ?? latestApplied;
}

function countFullCoverageRuns(rows: ExternalSyncRunRow[]): number {
  return rows.filter(isFullCoverageRun).length;
}

function isActiveFullCoverageRun(row: ExternalSyncRunRow | null): boolean {
  return Boolean(
    row &&
      row.status === 'apply_started' &&
      row.mode === 'apply' &&
      row.source_contract_version === 'admin-panel-full-refresh-v1',
  );
}

function isStale(staleAfter: string | null, now: Date): boolean {
  if (!staleAfter) return false;
  const parsed = new Date(staleAfter);
  return Number.isFinite(parsed.getTime()) && parsed.getTime() < now.getTime();
}

function pickLatestApplied(rows: ExternalSyncRunRow[]): ExternalSyncRunRow | null {
  return rows.find(isFullCoverageAppliedRun) ?? null;
}

function buildSourceFreshness(
  source: OutreachExternalSyncSource,
  rows: ExternalSyncRunRow[],
  now: Date
): OutreachExternalSyncFreshnessSource {
  const config = SOURCE_CONFIG[source];
  const latestRun = rows[0] ?? null;
  const latestApplied = pickLatestApplied(rows);
  const latestDisplayRun = pickLatestDisplayRun(source, rows, latestApplied);
  const totalRuns = source === 'customer_deal' ? countFullCoverageRuns(rows) : rows.length;

  if (isActiveFullCoverageRun(latestRun)) {
    return {
      source,
      label: config.label,
      description: config.description,
      requiredForExport: config.requiredForExport,
      contractVersion: latestRun?.source_contract_version ?? null,
      state: 'processing',
      latestStatus: parseStatus(latestRun?.status),
      latestMode: parseMode(latestRun?.mode),
      latestAppliedAt: latestApplied?.applied_at ?? null,
      latestCompletedAt: latestRun?.completed_at ?? null,
      staleAfter: latestApplied?.stale_after ?? null,
      totalRuns,
      rowCount: latestApplied ? sumCounts(latestApplied.row_counts) : null,
      actionCount: latestApplied ? sumCounts(latestApplied.action_counts) : null,
      invalidCount: latestApplied ? sumCounts(latestApplied.invalid_counts) : null,
      message: 'A full safety check is currently running.',
    };
  }

  if (!latestApplied) {
    const latestStatus = parseStatus(latestDisplayRun?.status);
    const failedLatest =
      config.requiredForExport && (latestStatus === 'failed' || latestStatus === 'partial_failure');
    const state: OutreachFreshnessState = failedLatest
      ? 'failed'
      : config.requiredForExport
        ? 'missing'
        : 'future_inactive';
    return {
      source,
      label: config.label,
      description: config.description,
      requiredForExport: config.requiredForExport,
      contractVersion: latestDisplayRun?.source_contract_version ?? null,
      state,
      latestStatus,
      latestMode: parseMode(latestDisplayRun?.mode),
      latestAppliedAt: null,
      latestCompletedAt: latestDisplayRun?.completed_at ?? null,
      staleAfter: latestDisplayRun?.stale_after ?? null,
      totalRuns,
      rowCount: latestDisplayRun ? sumCounts(latestDisplayRun.row_counts) : null,
      actionCount: latestDisplayRun ? sumCounts(latestDisplayRun.action_counts) : null,
      invalidCount: latestDisplayRun ? sumCounts(latestDisplayRun.invalid_counts) : null,
      message: failedLatest
        ? 'Latest run failed before an apply completed.'
        : config.requiredForExport
          ? latestStatus === 'dry_run_complete'
            ? 'No full safety check has processed leads yet.'
            : 'No applied full safety check recorded yet.'
          : config.futureInactiveMessage ?? 'Future source; not required for v1.',
    };
  }

  const latestStatus = parseStatus(latestDisplayRun?.status);
  const stale = isStale(latestApplied.stale_after, now);
  const failedAfterApply =
    latestDisplayRun !== latestApplied && (latestStatus === 'failed' || latestStatus === 'partial_failure');
  const state: OutreachFreshnessState = failedAfterApply ? 'failed' : stale ? 'stale' : 'fresh';

  return {
    source,
    label: config.label,
    description: config.description,
    requiredForExport: config.requiredForExport,
    contractVersion: latestApplied.source_contract_version,
    state,
    latestStatus: parseStatus(latestDisplayRun?.status),
    latestMode: parseMode(latestDisplayRun?.mode),
    latestAppliedAt: latestApplied.applied_at,
    latestCompletedAt: latestDisplayRun?.completed_at ?? latestApplied.completed_at,
    staleAfter: latestApplied.stale_after,
    totalRuns,
    rowCount: sumCounts(latestApplied.row_counts),
    actionCount: sumCounts(latestApplied.action_counts),
    invalidCount: sumCounts(latestApplied.invalid_counts),
    message: failedAfterApply
      ? 'Latest run needs review.'
      : stale
        ? 'Applied run is stale.'
        : 'Latest applied run is fresh.',
  };
}

function buildFreshnessSnapshot(rows: ExternalSyncRunRow[]): OutreachExternalSyncFreshnessSnapshot {
  const now = new Date();
  const rowsBySource = new Map<OutreachExternalSyncSource, ExternalSyncRunRow[]>(
    SOURCES.map((source) => [source, []])
  );

  for (const row of rows) {
    const source = parseSource(row.source);
    if (!source) continue;
    rowsBySource.get(source)?.push(row);
  }

  const sources = SOURCES.map((source) =>
    buildSourceFreshness(source, rowsBySource.get(source) ?? [], now)
  );
  const requiredFreshSources = sources
    .filter((source) => source.requiredForExport)
    .map((source) => source.source);
  const blockers = sources
    .filter((source) =>
      source.requiredForExport &&
      (source.state === 'missing' ||
        source.state === 'stale' ||
        source.state === 'processing' ||
        source.state === 'failed')
    )
    .map((source) => `${source.label}: ${source.message}`);
  const warnings = sources
    .filter((source) => !source.requiredForExport && source.state !== 'future_inactive' && source.state !== 'fresh')
    .map((source) => `${source.label}: ${source.message}`);

  return {
    generatedAt: now.toISOString(),
    environment: 'production',
    sources,
    exportGate: {
      state: blockers.length > 0 ? 'blocked' : warnings.length > 0 ? 'review' : 'ready',
      requiredFreshSources,
      blockers,
      warnings,
    },
  };
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error('Sign in again before refreshing Outreach Safety checks.');
  }
  return data.session.access_token;
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const accessToken = await getAccessToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(init.headers || {}),
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error?.message || `Outreach Safety request failed (${response.status}).`);
  }
  return payload as T;
}

function readCount(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`Contact classification returned invalid ${field}.`);
  }
  return value;
}

function readFingerprint(value: unknown, field: string): string {
  if (typeof value !== 'string' || !/^[0-9a-f]{64}$/.test(value)) {
    throw new Error(`Contact classification returned invalid ${field}.`);
  }
  return value;
}

function readReasonCounts(value: unknown): Partial<Record<LeadContactReadinessReason, number>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Contact classification returned invalid reason counts.');
  }
  const allowed = new Set<LeadContactReadinessReason>([
    'missing_person',
    'missing_email',
    'invalid_email',
    'role_email',
    'generic_email',
    'contact_ready',
  ]);
  const result: Partial<Record<LeadContactReadinessReason, number>> = {};
  for (const [reason, count] of Object.entries(value)) {
    if (!allowed.has(reason as LeadContactReadinessReason)) {
      throw new Error('Contact classification returned an unknown reason.');
    }
    result[reason as LeadContactReadinessReason] = readCount(count, `${reason} count`);
  }
  return result;
}

function readClassificationObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Contact classification returned an invalid response.');
  }
  const row = value as Record<string, unknown>;
  if (row.contract_version !== 'lead-contact-readiness-classification-v1') {
    throw new Error('Contact classification contract version is unsupported.');
  }
  return row;
}

function parseContactReadinessPreview(value: unknown): LeadContactReadinessClassificationPreview {
  const row = readClassificationObject(value);
  const unknownCount = readCount(row.unknown_count, 'unknown count');
  const projectedReadyCount = readCount(row.projected_ready_count, 'ready count');
  const projectedNeedsContactCount = readCount(row.projected_needs_contact_count, 'needs-contact count');
  if (projectedReadyCount + projectedNeedsContactCount !== unknownCount) {
    throw new Error('Contact classification preview counts do not reconcile.');
  }
  return {
    contractVersion: 'lead-contact-readiness-classification-v1',
    unknownCount,
    projectedReadyCount,
    projectedNeedsContactCount,
    reasonCounts: readReasonCounts(row.reason_counts),
    snapshotToken: readFingerprint(row.snapshot_token, 'snapshot token'),
    lifecyclePriorityFingerprint: readFingerprint(
      row.lifecycle_priority_fingerprint,
      'lifecycle and priority fingerprint',
    ),
  };
}

function parseContactReadinessResult(value: unknown): LeadContactReadinessClassificationResult {
  const row = readClassificationObject(value);
  const appliedCount = readCount(row.applied_count, 'applied count');
  const readyCount = readCount(row.ready_count, 'ready count');
  const needsContactCount = readCount(row.needs_contact_count, 'needs-contact count');
  if (readyCount + needsContactCount !== appliedCount || row.lifecycle_priority_unchanged !== true) {
    throw new Error('Contact classification apply result failed its safety checks.');
  }
  return {
    contractVersion: 'lead-contact-readiness-classification-v1',
    appliedCount,
    readyCount,
    needsContactCount,
    reasonCounts: readReasonCounts(row.reason_counts),
    queuedForSafetyCount: readCount(row.queued_for_safety_count, 'queued safety count'),
    lifecyclePriorityUnchanged: true,
    lifecyclePriorityFingerprint: readFingerprint(
      row.lifecycle_priority_fingerprint,
      'lifecycle and priority fingerprint',
    ),
  };
}

export const outreachExternalSyncService = {
  async getContactReadinessClassificationPreview(): Promise<LeadContactReadinessClassificationPreview> {
    const { data, error } = await supabase.rpc('preview_legacy_lead_contact_readiness');
    if (error) throw new Error(`Failed to preview contact classification: ${error.message}`);
    return parseContactReadinessPreview(data);
  },

  async applyContactReadinessClassification(
    snapshotToken: string,
  ): Promise<LeadContactReadinessClassificationResult> {
    const { data, error } = await supabase.rpc('apply_legacy_lead_contact_readiness', {
      p_snapshot_token: snapshotToken,
    });
    if (error) throw new Error(`Failed to classify legacy contacts: ${error.message}`);
    return parseContactReadinessResult(data);
  },

  async getFreshnessSnapshot(): Promise<OutreachExternalSyncFreshnessSnapshot> {
    const { data, error } = await supabase
      .from('outreach_external_sync_run_support')
      .select(
        'source,mode,status,source_contract_version,completed_at,applied_at,stale_after,row_counts,action_counts,invalid_counts,created_at'
      )
      .eq('environment', 'production')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('Error fetching outreach external sync run status:', error.message);
      throw new Error(`Failed to fetch outreach safety freshness: ${error.message}`);
    }

    return buildFreshnessSnapshot((data as unknown as ExternalSyncRunRow[]) || []);
  },

  async getQueueStatus(): Promise<OutreachSafetyQueueStatusResponse> {
    const payload = await requestJson<{ ok: true } & OutreachSafetyQueueStatusResponse>(
      '/api/outreach-safety/recompute-status',
    );
    return {
      queue: payload.queue,
      applyEnabled: Boolean(payload.applyEnabled),
    };
  },

  async requestManualRefresh(options: { limit?: number; scope?: 'full' | 'queued' } = {}): Promise<OutreachSafetyManualRefreshResult> {
    const payload = await requestJson<{ ok: true } & OutreachSafetyManualRefreshResult>(
      '/api/outreach-safety/recompute-worker',
      {
        method: 'POST',
        body: JSON.stringify({
          scope: options.scope ?? 'full',
          ...(options.limit ? { limit: options.limit } : {}),
        }),
      },
    );

    return {
      runId: payload.runId,
      scope: payload.scope,
      claimed: payload.claimed,
      completed: payload.completed,
      failed: payload.failed,
      totalTargets: payload.totalTargets ?? payload.claimed,
      queue: payload.queue,
    };
  },
};
